import { NgClass } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatTooltip } from '@angular/material/tooltip';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';
import { MarkdownComponent } from 'ngx-markdown';
import {
  Subscription,
  combineLatest,
  interval,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { distinctUntilChanged, map, scan } from 'rxjs/operators';
import * as fromGenerated from '../../_generated';
import { AccessRevokedNoticeService } from '../../access-revoked-notice.service';
import * as fromAnalytics from '../../analytics';
import * as fromAuth from '../../auth';
import * as fromLayout from '../../layout';
import * as fromAssistants from '../assistants';
import { ChatsHelper } from '../chats.helper';
import { ConversationCTAComponent } from '../components/conversation-cta.component';
import { ConversationHeaderDesktopResponsive } from '../components/conversation-header.desktop.responsive';
import { ImageOverlayViewerSmartComponent } from '../components/image-overlay-viewer/image-overlay-viewer.smart.component';
import { IntroductionComponent } from '../components/introduction.component';
import * as fromShared from '../shared';
import { selectSelectedChatDocuments } from '../shared';
import { Mappers } from '../shared/directives/file-upload-v2/file-upload-v2.helpers';
import {
  ChatActions,
  ChatWithMessagesActions,
  ChatsState,
  SelectDocumentInChatMessagePayload,
  chatsFeature,
} from '../store';
import { ConversationDragOverlayComponent } from './conversation-drag-overlay.component';
import { ConversationTopBarMobileResponsive } from './conversation-top-bar.mobile.responsive';
import { ConversationUrlSyncService } from './conversation-url-sync.service';
import { MessagesListingComponent } from './messages-listing';
import { ChatbotIsThinkingComponent } from './messages-listing/chatbot-is-thinking.component';
import {
  ImageInMessageClickedEvent,
  MessagesListingMessageComponent,
} from './messages-listing/message/message-listing-message.component';
import * as fromPromptInput from './prompt-input';

const LONG_REQUEST_POLLING_INTERVAL_IN_MS = 45 * 1000; // 45 seconds
const LONG_REQUEST_WARNING_THRESHOLD_IN_MS = 3 * 60 * 1000; // 3 minutes
const HEADER_POP_IN_THRESHOLD = 140; // in pixels
const MAX_SCROLL_THRESHOLD = 20; // wiggle room in px for "at bottom" detection
const MULTILINE_MESSAGE_HEIGHT_THRESHOLD = 72; // in px
const HEADER_HEIGHT = 80; // in px
const DOWNLOAD_ZIP_BUTTON_COOLDOWN_IN_MS = 3_000;

export interface DocumentInChatMessage {
  chatMessageId: number | null;
  documentUuid: string | null;
}

@Component({
  imports: [
    TranslateModule,
    fromSeriousSystem.IconButtonDirective,
    fromSeriousSystem.UseArrowIconDirective,
    fromSeriousSystem.UseLogoDirective,
    fromSeriousSystem.UseUIBasicIconDirective,
    fromPromptInput.ConversationPromptInputSmartComponent,
    MessagesListingComponent,
    fromSeriousSystem.ToastComponent,
    fromSeriousSystem.UseHeroIconOutlineDirective,
    ConversationHeaderDesktopResponsive,
    ConversationTopBarMobileResponsive,
    ConversationCTAComponent,
    IntroductionComponent,
    MarkdownComponent,
    NgClass,
    MatTooltip,
    ChatbotIsThinkingComponent,
    MessagesListingMessageComponent,
    ConversationDragOverlayComponent,
    ImageOverlayViewerSmartComponent,
  ],
  providers: [
    ChatsHelper,
    fromShared.FileUploadV2Store,
    fromShared.ChatImagePreviewStore,
  ],
  template: `
    <div
      #chat
      class="
        relative flex flex-col overflow-hidden
        h-full w-full max-h-dvh
      "
      [class.bg-gradient-e-vertical]="
        !(viewMode() === 'doc-viewer-conversation')
      "
      (dragover)="$event.preventDefault()"
    >
      <squadbox-conversation-drag-overlay
        [visible]="showDragOverlay()"
        [maxDocuments]="maxDocuments()"
      />
      @let assistant = selectedChatAssistant();
      @let chatWithTitle = selectedChatWithTitle();
      @let removedPromptInputState = promptInput()?.removedPromptInputState();
      @let ctaState = ctaRawState();

      @if (chatWithTitle) {
        <!-- TOP BAR -->
        @if (isMobile()) {
          <squadbox-conversation-top-bar-mobile
            [selectedChatTitle]="chatWithTitle.title"
            [selectedAssistant]="selectedChatAssistant()"
            [isNewConversation]="!chatWithTitle._count.messages"
          />
        } @else {
          <squadbox-conversation-header-desktop
            class="absolute"
            [selectedChatTitle]="chatWithTitle.title"
            [selectedAssistant]="selectedChatAssistant()"
            [showTitle]="showHeaderTitle()"
            [animate]="animateHeader()"
          />
        }
        <div
          class="
            flex flex-col grow overflow-hidden justify-between
            tablet-landscape:pb-4 pb-2
          "
        >
          <!-- MAIN CONTENT: MESSAGES LISTING, SPACER FOR LAST MESSAGE, CTA -->
          <div
            #messageContainer
            class="
              flex flex-col grow
              items-center
              w-full max-w-svw overflow-y-auto
              text-h5 px-4 pt-4 tablet-landscape:pt-0
            "
            [class.gap-8]="!assistant"
            [class.gap-6]="assistant"
            [ngClass]="{
              'justify-center':
                isMobile() && isNewConversationWithoutAssistant(),
              'justify-end pb-8':
                !isMobile() && isNewConversationWithoutAssistant(),
            }"
            (scroll)="handleOnScroll()"
          >
            @if (isNewConversationWithoutAssistant()) {
              <!-- First login screen, shows the company logo as well as a welcome message -->

              <img
                class="h-11.5"
                sdUseLogo="logo-new"
                [alt]="'CHAT.COMPANY_LOGO_ALT' | translate"
              />

              <div class="w-full flex flex-col items-center text-center">
                <span
                  class="tablet-landscape:typo-h4 typo-h6 font-semibold leading-8"
                  >{{ 'CHAT.HELLO' | translate }} {{ userName() }}
                </span>
              </div>
            } @else {
              @if (assistant) {
                <div
                  class="
                    flex flex-col w-full
                    tablet-landscape:gap-6 gap-4
                    tablet-landscape:pt-22 pt-2
                    tablet-landscape:max-w-180
                  "
                >
                  <squadbox-introduction
                    [imagePath]="assistant.logo"
                    [title]="assistant.name"
                  />
                  @if (isNewConversationWithAssistant()) {
                    <markdown
                      class="typo-p2 p-2 max-w-full"
                      [data]="assistant.introMessage ?? assistant.description"
                    />
                  }
                </div>
              }
              @if (!isNewConversationWithAssistant()) {
                <!-- Messages List -->
                <div
                  class="
                    flex flex-col gap-8 tablet-landscape:gap-4
                    w-full tablet-landscape:max-w-180
                  "
                  [class.tablet-landscape:pt-22]="!assistant"
                  [class.opacity-0]="isRepositioning()"
                >
                  <squadbox-messages-listing
                    [messages]="selectedChatMessages()"
                    [isLoading]="isLoading()"
                    [isThinking]="chatWithTitle.isThinking"
                    [isMobile]="isMobile()"
                    [messageCount]="
                      chatWithTitle._count.messages ||
                      selectedChatMessages().length
                    "
                    [selectedDocumentInChatMessage]="
                      selectedDocumentInChatMessage()
                    "
                    (documentInChatMessageClicked)="
                      handleDocumentInChatMessageClicked($event)
                    "
                    (imageInChatMessageClicked)="
                      handleImageInChatMessageClicked($event)
                    "
                    (newReplyAnimationComplete)="
                      handleNewReplyAnimationComplete()
                    "
                    (spacerShouldRecalculate)="onSpacerShouldRecalculate()"
                  />
                  @if (chatWithTitle.isThinking) {
                    <squadbox-chatbot-is-thinking
                      [text]="'CHAT.LOADING' | translate"
                    />
                  }
                </div>
              }
              @if (ctaState.shouldShow) {
                <squadbox-conversation-cta
                  class="block w-full tablet-landscape:max-w-180 fade-in"
                  [mainIcon]="ctaState.mainIcon"
                  [mainText]="
                    ctaState.mainTextKey | translate: ctaState.mainTextParams
                  "
                  [subText]="
                    ctaState.subTextKey
                      ? (ctaState.subTextKey
                        | translate: ctaState.subTextParams)
                      : null
                  "
                  [buttonIcon]="ctaState.buttonIcon"
                  [buttonText]="
                    ctaState.buttonTextKey
                      | translate: ctaState.buttonTextParams
                  "
                  [isDisabled]="ctaState.buttonIsDisabled"
                  (clicked)="promptInput?.triggerFileUpload('chat_cta')"
                />
              }
              @if (
                removedPromptInputState &&
                removedPromptInputState.message &&
                removedPromptInputState.shouldShowTransientFirstMessageWithDocuments
              ) {
                <div
                  class="
                    flex flex-col gap-4
                    w-full tablet-landscape:max-w-180
                  "
                >
                  <squadbox-messages-listing-message
                    [isFromUser]="removedPromptInputState.message.isFromUser"
                    [messageId]="removedPromptInputState.message.id"
                    [messageContent]="removedPromptInputState.message.content"
                    [messageDocuments]="
                      removedPromptInputState.message.documents
                    "
                  />
                  @if (
                    removedPromptInputState.shouldShowChatbotThinkingForDocuments
                  ) {
                    <squadbox-chatbot-is-thinking
                      [text]="'CHAT.DOCUMENTS_PROCESSING' | translate"
                    />
                  }
                </div>
              }
            }
            <div
              [style.height.px]="bottomSpacerHeightPx()"
              [class.-mt-8]="!assistant"
              [class.-mt-6]="assistant"
              class="w-full shrink-0 message-spacer-bottom"
            ></div>
          </div>
          <!-- FOOTER: DISCLAIMER, SCROLL BUTTON, TOASTS, INPUT PROMPT -->
          <div
            class="
              flex flex-col justify-between items-center
              gap-3 tablet-landscape:gap-4 w-full max-w-svw px-4
            "
            [class]="{
              grow: isNewConversationWithoutAssistant() && !isMobile(),
            }"
          >
            <div
              class="relative flex flex-col justify-center w-full tablet-portrait:max-w-180"
            >
              <!-- SCROLL BUTTON -->
              @if (showScrollButton()) {
                <button
                  sdIconButton
                  variant="outlined"
                  color="neutral"
                  size="xs"
                  class="absolute bottom-full left-1/2 -translate-x-1/2 mb-12 tablet-portrait:mb-10 z-20 bg-shades-white"
                  (click)="scrollToBottom('smooth')"
                >
                  <svg sdUseArrowIcon="arrow-down"></svg>
                </button>
              }
              @if (lastSelectedChatMessage(); as lastMessage) {
                @if (lastMessage.error && !lastMessage.errorDismissed) {
                  <sd-toast
                    class="fade-in relative mb-4 z-10 self-center max-w-fit"
                    [message]="lastMessage.error"
                    [type]="'sd-toast-error'"
                    (dismissClicked)="handleChatErrorToastDismissClicked()"
                  />
                }
              }
              <!-- FILE UPLOAD ERROR TOAST -->
              @if (documentErrorToastState().isVisible) {
                <sd-toast
                  class="fade-in relative mb-2 z-10 self-center max-w-fit"
                  [class.mb-17]="promptInput?.tooltipOverPromptText()"
                  [message]="documentErrorToastState().message"
                  [dismissible]="documentErrorToastState().dismissable"
                  (dismissClicked)="handleDocumentErrorToastDismissClicked()"
                  [type]="'sd-toast-error'"
                />
              } @else if (longRequestWarningVisible()) {
                <sd-toast
                  class="fade-in relative mb-2 z-10 self-center max-w-fit"
                  [class.mb-17]="promptInput?.tooltipOverPromptText()"
                  [message]="'CHAT.LONG_REQUEST_WARNING_MESSAGE' | translate"
                  [dismissible]="true"
                  (dismissClicked)="handleLongRequestWarningDismissClicked()"
                />
              }

              <!-- INPUT PROMPT -->
              <div
                class="w-full"
                [style.view-transition-name]="'prompt-input'"
                [class.hidden]="promptInputHidden()"
              >
                <squadbox-conversation-prompt-input-smart
                  #promptInput
                  [isThinking]="chatWithTitle.isThinking"
                  [isNewReplyAnimating]="isNewReplyAnimating()"
                />
              </div>
            </div>

            <!-- FOOTER -->
            <!-- FAB -->
            @if (promptInputHidden()) {
              <div class="fixed bottom-4 right-4 flex flex-col gap-2">
                @if (assistant?.trait === 'extract') {
                  <button
                    sdIconButton
                    variant="outlined"
                    color="neutral"
                    class="bg-shades-white shadow-fab border-neutral-200"
                    [matTooltip]="
                      'PROMPT_INPUT.TOOLTIP.DOWNLOAD_ALL' | translate
                    "
                    matTooltipPosition="before"
                    [matTooltipDisabled]="
                      lastChatbotMessageOutputDocumentsUuids().length === 0
                    "
                    [disabled]="
                      lastChatbotMessageOutputDocumentsUuids().length === 0 ||
                      isDownloadingZip()
                    "
                    (click)="downloadLastChatbotMessageOutputDocuments()"
                  >
                    <svg sdUseHeroIconOutline="arrow-down-tray"></svg>
                  </button>
                  <button
                    sdIconButton
                    variant="outlined"
                    color="neutral"
                    class="bg-shades-white shadow-fab border-neutral-200"
                    matTooltipPosition="before"
                    [matTooltip]="
                      fileUploadDisabled().disabled &&
                      fileUploadDisabled().i18nKey
                        ? (fileUploadDisabled().i18nKey
                          | translate: fileUploadDisabled().i18nParams)
                        : ''
                    "
                    (click)="promptInput?.triggerFileUpload('chat_fab')"
                    [disabled]="fileUploadDisabled().disabled"
                  >
                    <svg sdUseUIBasicIcon="plus"></svg>
                  </button>
                }
              </div>
            }

            <!-- DISCLAIMER -->
            <div
              class="
                text-center
                typo-caption tablet-landscape:typo-p3
                text-neutral-500 tablet-landscape:text-neutral-500
              "
              [innerHTML]="
                (isMobile() || viewMode() === 'doc-viewer-conversation'
                  ? 'CHAT.DISCLAIMER_SHORT'
                  : 'CHAT.DISCLAIMER_LONG'
                ) | translate
              "
            ></div>
          </div>
        </div>
      }
    </div>

    <!-- Full-screen image overlay viewer — fixed-position, opened
         via ImageOverlayActions from an image chip (thread or composer).
         MUST stay inside the ConversationPage injector: it shares this page's
         FileUploadV2Store (providers above) to read a staged image's raw File
         for the pre-send preview. A global/other mount would blank previews. -->
    <squadbox-image-overlay-viewer-smart />
  `,
  styles: [
    `
      ::-webkit-scrollbar {
        display: none;
      }
    `,
  ],
})
export class ConversationPage implements OnInit, OnDestroy {
  private readonly translate = inject(TranslateService);
  private readonly accessRevokedNotice = inject(AccessRevokedNoticeService);
  private readonly authStore = inject<Store<fromAuth.AuthState>>(Store);
  private readonly chatsStore = inject<Store<ChatsState>>(Store);
  private readonly assistantsStore =
    inject<Store<fromAssistants.AssistantsState>>(Store);
  private readonly layoutStore = inject<Store<fromLayout.LayoutState>>(Store);
  private readonly chatsHelper = inject(ChatsHelper);
  private readonly conversationUrlSyncService = inject(
    ConversationUrlSyncService,
  );
  private readonly fileUploadStore = inject(fromShared.FileUploadV2Store);
  private readonly destroyRef = inject(DestroyRef);
  private readonly documentsService = inject(fromGenerated.DocumentsService);
  private readonly analyticsService = inject(fromAnalytics.AnalyticsService);

  public readonly promptInput =
    viewChild<fromPromptInput.ConversationPromptInputSmartComponent>(
      'promptInput',
    );
  private readonly messageContainer =
    viewChild<ElementRef<HTMLDivElement>>('messageContainer');

  public readonly selectedChatId = this.chatsStore.selectSignal(
    chatsFeature.selectSelectedChatId,
  );
  private readonly stateLoaded = computed(
    () =>
      this.assistantsStore.selectSignal(
        fromAssistants.assistantsFeature.selectLoaded,
      )() && this.chatsStore.selectSignal(chatsFeature.selectLoaded)(),
  );

  public readonly isMobile = this.layoutStore.selectSignal(
    fromLayout.layoutFeature.selectIsMobile,
  );
  public readonly viewMode = this.layoutStore.selectSignal(
    fromLayout.layoutFeature.selectViewMode,
  );
  public readonly selectedChatAssistant = this.chatsStore.selectSignal(
    fromShared.selectSelectedChatAssistant,
  );
  protected readonly compliancePhase = this.chatsStore.selectSignal(
    fromShared.selectComplianceUploadPhase,
  );
  public readonly isLoading = this.chatsStore.selectSignal(
    chatsFeature.selectIsLoading,
  );
  public readonly selectedDocumentInChatMessage = this.chatsStore.selectSignal(
    chatsFeature.selectSelectedDocumentInChatMessage,
  );
  public readonly maxDocuments = computed(() => {
    return this.chatsStore.selectSignal(fromShared.selectDocumentLimits)().max;
  });
  private readonly localFilesUuids = computed(() =>
    this.fileUploadStore.getFiles().map((f) => f.uuid),
  );
  /** CTA block: state from selector (template translates via pipe and handles click via promptInput). */
  protected readonly ctaRawState = computed(() =>
    this.chatsStore.selectSignal(
      fromShared.selectConversationCtaState(this.localFilesUuids()),
    )(),
  );
  protected readonly fileUploadDisabled = computed(() =>
    this.chatsStore.selectSignal(
      fromShared.selectFileUploadDisabled(this.localFilesUuids()),
    )(),
  );
  protected readonly promptInputHidden = this.chatsStore.selectSignal(
    fromShared.selectPromptInputHidden,
  );

  private readonly user = this.authStore.selectSignal(
    fromAuth.authFeature.selectUser,
  );

  public readonly userName = computed(() => {
    return `${this.user()?.firstName}`;
  });
  public readonly selectedChatWithTitle = computed(() => {
    const selectedChat = this.chatsStore.selectSignal(
      chatsFeature.selectSelectedChat,
    )();

    return selectedChat
      ? {
          ...selectedChat,
          title: this.chatsHelper.getChatTitle(selectedChat.title),
        }
      : null;
  });
  public readonly selectedChatMessages = computed(
    () => this.selectedChatWithTitle()?.messages ?? [],
  );
  public readonly isNewConversationWithoutAssistant =
    this.chatsStore.selectSignal(
      fromShared.selectIsNewConversationWithoutAssistant,
    );
  public readonly isNewConversationWithAssistant = this.chatsStore.selectSignal(
    fromShared.selectIsNewConversationWithAssistant,
  );
  public readonly lastSelectedChatMessage = computed(() => {
    const selectedChat = this.selectedChatWithTitle();
    return selectedChat?.messages?.[selectedChat?.messages.length - 1] ?? null;
  });
  public readonly isLastMessageFromUser = computed(() => {
    return this.lastSelectedChatMessage()?.isFromUser ?? false;
  });
  public readonly documentErrorToastState = computed(() => {
    const promptInputErrorState =
      this.promptInput()?.fileUploadErrorToastState() ?? {
        isVisible: false,
        message: '',
        dismissable: false,
      };

    if (this.downloadError()) {
      return {
        isVisible: true,
        message: [promptInputErrorState.message, this.downloadError()]
          .filter(Boolean)
          .join(' '),
        dismissable: true,
      };
    }

    return promptInputErrorState;
  });
  public readonly lastChatbotMessageOutputDocumentsUuids = computed(() => {
    const selectedChatDocuments = this.chatsStore.selectSignal(
      selectSelectedChatDocuments,
    )();
    return selectedChatDocuments.lastChatbotMessageOutputDocuments.map(
      (d) => d.uuid,
    );
  });

  public readonly showScrollButton = signal(false);
  public readonly showHeaderTitle = signal(true);
  public readonly animateHeader = signal(false);
  public readonly isScrolledToBottom = signal(false);
  public readonly isRepositioning = signal(false);

  public readonly isNewReplyAnimating = this.chatsStore.selectSignal(
    chatsFeature.selectIsLastMessageAnimating,
  );

  public readonly isDraggingFiles = signal(false);
  private dragDepth = 0;
  public readonly showDragOverlay = computed(
    () =>
      this.isDraggingFiles() &&
      !this.fileUploadDisabled().disabled &&
      this.viewMode() === 'conversation',
  );
  private readonly downloadError = signal<string | null>(null);
  public readonly isDownloadingZip = signal(false);

  private hasFiles(event: DragEvent): boolean {
    const types = event.dataTransfer?.types;
    return !!types && Array.from(types).includes('Files');
  }

  private resetDragUi(): void {
    this.isDraggingFiles.set(false);
    this.dragDepth = 0;
  }

  @HostListener('window:dragenter', ['$event'])
  onWindowDragEnter(event: DragEvent): void {
    if (!this.hasFiles(event)) return;
    event.preventDefault();
    this.dragDepth++;
    if (
      this.fileUploadDisabled().disabled ||
      this.viewMode() !== 'conversation'
    )
      return;
    this.isDraggingFiles.set(true);
  }

  @HostListener('window:dragover', ['$event'])
  onWindowDragOver(event: DragEvent): void {
    if (!this.hasFiles(event)) return;
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = this.fileUploadDisabled().disabled
        ? 'none'
        : 'copy';
    }
    if (
      this.fileUploadDisabled().disabled ||
      this.viewMode() !== 'conversation'
    )
      return;
    this.isDraggingFiles.set(true);
  }

  @HostListener('window:dragleave', ['$event'])
  onWindowDragLeave(event: DragEvent): void {
    if (!this.hasFiles(event)) return;
    this.dragDepth = Math.max(0, this.dragDepth - 1);
    if (this.dragDepth === 0) {
      this.resetDragUi();
    }
  }

  @HostListener('window:drop', ['$event'])
  onWindowDrop(event: DragEvent): void {
    if (!this.hasFiles(event)) return;
    event.preventDefault();
    if (
      this.fileUploadDisabled().disabled ||
      this.viewMode() !== 'conversation'
    ) {
      this.resetDragUi();
      return;
    }

    const dt = event.dataTransfer;
    const files: File[] = [];

    if (dt?.items?.length) {
      for (const item of Array.from(dt.items)) {
        if (item.kind === 'file') {
          const f = item.getAsFile();
          if (f) files.push(f);
        }
      }
    } else if (dt?.files?.length) {
      files.push(...Array.from(dt.files));
    }

    if (files.length) {
      const mapped = files.map((file) => Mappers.toFileUploadStateFile(file));
      this.fileUploadStore.addFiles(mapped);
      // Immediately upload valid files (mirrors the trigger directive behavior)
      this.fileUploadStore
        .uploadValidFiles()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          error: (err) => {
            console.error('Upload failed after drop:', err);
          },
        });
    }

    this.resetDragUi();
  }

  public readonly bottomSpacerHeightPx = signal<number>(0);

  /**
   * Recalculates spacer height and scroll button visibility.
   * Called during new reply markdown animation and when it completes.
   */
  public onSpacerShouldRecalculate(): void {
    this.calculateSpacerHeight();
    this.handleOnScroll();
  }

  /**
   * Calculates and sets the bottom spacer height so the last user message stays
   * visible in the viewport. Used when new reply content loads (e.g. during
   * markdown streaming) to avoid scrolling the last user message out of view.
   *
   * Uses the scroll anchor element placed after the message bubble to compute
   * how much spacer is needed. Updates {@link bottomSpacerHeightPx} with the
   * computed height in pixels.
   */
  public calculateSpacerHeight(): void {
    const container = this.messageContainer()?.nativeElement;
    const spacer = container?.querySelector<HTMLElement>(
      '.message-spacer-bottom',
    );
    const lastUserMessage = this.selectedChatMessages()
      .filter((m) => m.isFromUser)
      .at(-1);
    const anchor = lastUserMessage
      ? document.getElementById(`scroll-anchor-${lastUserMessage.id}`)
      : null;

    if (!container || !spacer || !anchor) {
      if (this.bottomSpacerHeightPx() !== 0) {
        this.bottomSpacerHeightPx.set(0);
      }
      return;
    }

    const SCROLL_MARGIN = HEADER_HEIGHT + MULTILINE_MESSAGE_HEIGHT_THRESHOLD;
    const contentBelowAnchor =
      spacer.getBoundingClientRect().top - anchor.getBoundingClientRect().top;
    const needed = Math.max(
      0,
      container.clientHeight - SCROLL_MARGIN - contentBelowAnchor,
    );

    if (this.bottomSpacerHeightPx() !== needed) {
      this.bottomSpacerHeightPx.set(needed);
    }
  }
  /**
   * null: not shown, false: dismissed, true: shown
   */
  public readonly longRequestWarningVisible = signal<boolean | null>(null);
  private readonly pollingState$ = combineLatest([
    toObservable(this.selectedChatWithTitle),
    toObservable(this.lastSelectedChatMessage),
  ]).pipe(
    map(([chat, lastMessage]) => ({
      id: chat?.id,
      isThinking: chat?.isThinking,
      error: lastMessage?.error ?? null,
      shouldPoll: !!(chat?.isThinking && !lastMessage?.error && chat?.id),
    })),
  );
  private pollingSubscription?: Subscription;

  constructor() {
    // Sync URL with state when the state is loaded (only once)
    let hasSyncedOnce = false;
    effect(() => {
      if (this.stateLoaded() && !hasSyncedOnce) {
        hasSyncedOnce = true;
        void this.conversationUrlSyncService.syncUrlWithState();
      }
    });

    /**
     * Monitors chat messages changes to update UI state
     * (header title visibility, thinking state, scroll to bottom and scroll button visibility)
     */
    effect(() => {
      this.selectedChatId();
      const loading = this.isLoading();

      // Hide messages while scrolling to the correct position to prevent flash.
      // Scroll to the bottom on page load. No smoothing.
      if (!loading) {
        this.isRepositioning.set(true);
        setTimeout(() => {
          this.repositionToLastUserMessage('auto', true);
        }, 0);
      }
    });

    // Scroll to the last user message when a new user message appears (same as when loading)
    let prevMessageCount = 0;
    effect(() => {
      const messages = this.selectedChatMessages();
      const count = messages.length;
      const lastIsFromUser = messages.at(-1)?.isFromUser ?? false;

      if (
        lastIsFromUser &&
        !this.isLoading() &&
        count === prevMessageCount + 1
      ) {
        prevMessageCount = count;

        // This is a workaround to ensure the new user message is rendered before scrolling to it
        // Defer to next tick so the DOM has rendered the new message, then smooth-scroll
        // for better UX. A second 'auto' scroll after 150ms snaps to the final position
        // once layout has settled (e.g. markdown rendering or other animations).
        setTimeout(() => {
          this.repositionToLastUserMessage('smooth');
          setTimeout(() => {
            this.repositionToLastUserMessage('auto');
          }, 150);
        }, 0);
      } else {
        prevMessageCount = count;
      }
    });

    effect(() => {
      // Track signals for effect evaluation by invoking them
      this.selectedChatMessages();
      this.selectedChatWithTitle();
      this.ctaRawState();
      this.promptInput()?.removedPromptInputState?.();
    });

    // Track when thinking stops so we can keep submit disabled until markdown animation completes
    let prevIsThinking: boolean | null = null;
    let prevChatId: number | null | undefined = undefined;
    effect(() => {
      const chatId = this.selectedChatId();
      const chatWithTitle = this.selectedChatWithTitle();
      const isThinking = chatWithTitle?.isThinking ?? false;
      const stoppedThinking = prevIsThinking === true && !isThinking;

      if (prevChatId !== undefined && prevChatId !== chatId) {
        this.chatsStore.dispatch(
          ChatActions.setLastMessageAnimating({ isAnimating: false }),
        );
      } else if (stoppedThinking) {
        this.chatsStore.dispatch(
          ChatActions.setLastMessageAnimating({ isAnimating: true }),
        );
      }
      prevIsThinking = isThinking;
      prevChatId = chatId;
    });

    // Active-view access loss: the skill behind the open conversation was
    // revoked live. The chat still references an assistantId, but it no longer
    // resolves in the (refreshed) assistants store — so send the user to the
    // landing page with an "access removed" toast instead of silently rendering
    // a chat they can no longer use. Gated on `stateLoaded` (both stores are
    // replace-on-success, so there is no transient-empty window) and on the
    // chat actually having an assistant (general chats legitimately have none).
    effect(() => {
      if (!this.stateLoaded()) {
        return;
      }
      const chat = this.selectedChatWithTitle();
      if (!chat?.assistantId) {
        return;
      }
      if (!this.selectedChatAssistant()) {
        void this.accessRevokedNotice.notify('TOAST.ACCESS_REVOKED.SKILL');
      }
    });
  }

  ngOnInit() {
    this.pollingSubscription = this.pollingState$
      .pipe(
        distinctUntilChanged(
          (prev, curr) =>
            prev.id === curr.id &&
            prev.shouldPoll === curr.shouldPoll &&
            prev.isThinking === curr.isThinking &&
            prev.error === curr.error,
        ),
        switchMap((state) => {
          // Hide warning when polling stops
          if (!state.shouldPoll) {
            if (this.longRequestWarningVisible() !== null) {
              if (document.startViewTransition) {
                document.startViewTransition(() => {
                  this.longRequestWarningVisible.set(null);
                });
              } else {
                this.longRequestWarningVisible.set(null);
              }
            }
            // Only create interval when shouldPoll is true, otherwise emit nothing
            return of(null);
          }

          return interval(LONG_REQUEST_POLLING_INTERVAL_IN_MS).pipe(
            // Accumulate total polling time using scan; triggers a long request warning if the threshold is reached
            scan((acc) => acc + LONG_REQUEST_POLLING_INTERVAL_IN_MS, 0),
            tap((totalTime) => {
              if (
                totalTime >= LONG_REQUEST_WARNING_THRESHOLD_IN_MS &&
                this.longRequestWarningVisible() === null
              ) {
                if (document.startViewTransition) {
                  document.startViewTransition(() => {
                    this.longRequestWarningVisible.set(true);
                  });
                } else {
                  this.longRequestWarningVisible.set(true);
                }
              }
              if (state.id) {
                this.chatsStore.dispatch(
                  ChatWithMessagesActions.loadChatWithMessages({
                    id: state.id,
                    withoutIsLoading: true,
                  }),
                );
              }
            }),
          );
        }),
      )
      .subscribe();
  }

  public handleLongRequestWarningDismissClicked(): void {
    if (document.startViewTransition) {
      document.startViewTransition(() => {
        this.longRequestWarningVisible.set(null);
      });
    } else {
      this.longRequestWarningVisible.set(null);
    }
  }

  protected handleOnScroll(): void {
    this.updateIsScrolledToBottom();
    this.updateHeaderVisibility();

    this.showScrollButton.set(!this.isScrolledToBottom());
  }

  /**
   * Updates the signal flagging whether the user is scrolled to the bottom of the page.
   *
   * We consider at the bottom when the top of the spacer is at or near the bottom of
   * the visible container. There is MAX_SCROLL_THRESHOLD pixels of wiggle room allowed.
   *
   * @returns void
   */
  private updateIsScrolledToBottom(): void {
    const messageContainer = this.messageContainer()?.nativeElement;
    if (!messageContainer) return;

    // If it's a new conversation, set it to true
    if (this.selectedChatWithTitle()?._count?.messages === 0) {
      this.isScrolledToBottom.set(true);
      return;
    }

    const spacer = messageContainer.querySelector<HTMLElement>(
      '.message-spacer-bottom',
    );
    if (spacer) {
      const containerBottom = messageContainer.getBoundingClientRect().bottom;
      const spacerTop = spacer.getBoundingClientRect().top;
      this.isScrolledToBottom.set(
        spacerTop <= containerBottom + MAX_SCROLL_THRESHOLD,
      );
    } else {
      this.isScrolledToBottom.set(
        messageContainer.scrollTop >=
          messageContainer.scrollHeight -
            messageContainer.clientHeight -
            MAX_SCROLL_THRESHOLD,
      );
    }
  }

  /**
   * Updates the header visibility & animation status based on the scroll position, view mode and whether an assistant is present.
   *
   * The header is always visible in PDF modes. If with an assistant, it's based on scroll position
   *   and otherwise based on whether the conversation is new.
   *
   * The header only animates if the conversation can scroll and is with an assistant (with messages).
   *
   * @returns void
   */
  private updateHeaderVisibility(): void {
    const messageContainer = this.messageContainer()?.nativeElement;
    if (!messageContainer) return;

    // Only the conversation mode handles header visibility. It's always visible in PDF modes.
    if (this.viewMode() !== 'conversation') {
      this.showHeaderTitle.set(true);
      this.animateHeader.set(false);
      return;
    }

    // If chatting with an assistant, the visibility is based on scroll position.
    // Otherwise, it's based on whether the conversation is new (AKA has messages)
    if (this.selectedChatAssistant()) {
      this.showHeaderTitle.set(
        messageContainer.scrollTop >= HEADER_POP_IN_THRESHOLD,
      );
    } else {
      this.showHeaderTitle.set(
        this.selectedChatWithTitle()?._count?.messages !== 0,
      );
    }

    // Animate the header only if:
    // - An assistant conversation with messages is selected
    // - The message container is able to scroll
    this.animateHeader.set(
      this.selectedChatWithTitle()?._count?.messages !== 0 &&
        !!this.selectedChatAssistant() &&
        messageContainer.scrollHeight > messageContainer.clientHeight,
    );
  }

  public scrollToBottom(behavior: 'smooth' | 'auto' = 'auto'): void {
    try {
      const messageContainer = this.messageContainer()?.nativeElement;
      if (messageContainer) {
        setTimeout(() => {
          messageContainer.scrollTo({
            top: messageContainer.scrollHeight,
            behavior,
          });
        }, 0);
      }
    } catch (err) {
      console.error('Could not scroll to bottom:', err);
    }
  }

  /**
   * Updates the spacer height and scrolls to the last user message. Uses two ticks:
   * first tick sets the spacer so the DOM layout is correct, second tick performs
   * the scroll after the layout has been applied.
   */
  private repositionToLastUserMessage(
    behavior: 'smooth' | 'auto',
    clearRepositioning?: boolean,
  ): void {
    untracked(() => this.calculateSpacerHeight());
    setTimeout(() => {
      untracked(() => {
        this.scrollToLastUserMessage(behavior);
        this.handleOnScroll();
        if (clearRepositioning) {
          this.isRepositioning.set(false);
        }
      });
    }, 0);
  }

  /**
   * Scrolls the last user message's scroll anchor into view. The anchor's
   * CSS scroll-margin-top handles the header + visible-text offset natively.
   *
   * @param behavior - Scroll behavior. https://developer.mozilla.org/en-US/docs/Web/CSS/scroll-behavior
   */
  private scrollToLastUserMessage(behavior: 'smooth' | 'auto' = 'auto'): void {
    const lastUserMessage = this.selectedChatMessages()
      .filter((m) => m.isFromUser)
      .at(-1);
    const anchor = lastUserMessage
      ? document.getElementById(`scroll-anchor-${lastUserMessage.id}`)
      : null;

    if (anchor) {
      anchor.scrollIntoView({ block: 'start', behavior });
    }
  }

  public handleDocumentInChatMessageClicked(
    event: SelectDocumentInChatMessagePayload,
  ) {
    this.chatsStore.dispatch(
      fromShared.DocumentViewerActions.openDocumentFromChatMessage({
        chatMessageId: event.chatMessageId,
        documentUuid: event.documentUuid,
      }),
    );

    setTimeout(() => {
      this.scrollToSelectedMessage();
    }, 0);
  }

  public handleImageInChatMessageClicked(event: ImageInMessageClickedEvent) {
    this.chatsStore.dispatch(
      fromShared.ImageOverlayActions.openImageFromChatMessage({
        documentUuid: event.documentUuid,
        filename: event.filename,
      }),
    );
    // Track overlay open from an in-thread image chip.
    this.analyticsService
      .sendImageOverlayOpenedEvent({ source: 'chip' })
      .subscribe();
  }

  public handleNewReplyAnimationComplete() {
    this.onSpacerShouldRecalculate();
    this.chatsStore.dispatch(
      ChatActions.setLastMessageAnimating({ isAnimating: false }),
    );
  }

  public handleChatErrorToastDismissClicked() {
    const chatId = this.selectedChatId();

    if (!chatId) {
      return;
    }

    this.chatsStore.dispatch(
      ChatWithMessagesActions.dismissLastChatMessageError({
        chatId,
      }),
    );
  }

  public handleDocumentErrorToastDismissClicked() {
    this.downloadError.set(null);

    const chatId = this.selectedChatId();

    if (!chatId) {
      return;
    }

    const resetFileUploadAndRemoveChatDocuments = () => {
      this.fileUploadStore.reset();
      this.chatsStore.dispatch(
        ChatWithMessagesActions.removeDocuments({
          chatId,
        }),
      );
    };

    if (!document.startViewTransition) {
      resetFileUploadAndRemoveChatDocuments();
      return;
    }

    document.startViewTransition(() => {
      resetFileUploadAndRemoveChatDocuments();
    });
  }

  private scrollToSelectedMessage(): void {
    const selectedDoc = this.selectedDocumentInChatMessage();

    if (selectedDoc?.chatMessageId === null) {
      return;
    }

    const messageIdElement = document.getElementById(
      `message-${selectedDoc.chatMessageId}`,
    );

    if (!messageIdElement) {
      return;
    }

    messageIdElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }

  public async downloadLastChatbotMessageOutputDocuments(): Promise<void> {
    const documentUuids = this.lastChatbotMessageOutputDocumentsUuids();

    if (documentUuids.length === 0 || this.isDownloadingZip()) {
      return;
    }

    this.downloadError.set(null);
    this.isDownloadingZip.set(true);

    try {
      const configuration = this.documentsService.configuration;
      const baseUrl = configuration.basePath;
      const url = new URL(
        `${baseUrl}/v1/documents/download-zip`,
        globalThis.location.origin,
      );
      const requestBody = new URLSearchParams();
      documentUuids.forEach((uuid) =>
        requestBody.append('documentUuids', uuid),
      );

      // Validate the request as soon as headers arrive, then let the browser
      // take over the real download so the zip can start streaming immediately.
      const controller = new AbortController();
      const response = await fetch(url.toString(), {
        method: 'POST',
        body: requestBody,
        signal: controller.signal,
        credentials: configuration.withCredentials ? 'include' : 'same-origin',
      });

      if (!response.ok) {
        let errorData: {
          statusCode: number;
          message: string;
        };
        try {
          errorData = (await response.json()) as {
            statusCode: number;
            message: string;
          };
        } catch {
          errorData = {
            statusCode: response.status,
            message: `Server returned ${response.status}`,
          };
        }
        throw new Error(
          errorData.message ?? `Server returned ${response.status}`,
        );
      }

      // Stop reading the probe response body so the browser does not continue
      // draining the zip stream before we hand off to the native download.
      await response.body
        ?.cancel()
        .catch((error) => console.error('body cancel failed', error));
      controller.abort();
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = url.toString();
      form.style.display = 'none';
      documentUuids.forEach((uuid) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'documentUuids';
        input.value = uuid;
        form.appendChild(input);
      });
      document.body.appendChild(form);
      form.submit();
      form.remove();

      // Native browser downloads do not expose a reliable completion callback,
      // so keep the button disabled briefly to prevent duplicate requests.
      globalThis.setTimeout(() => {
        this.isDownloadingZip.set(false);
      }, DOWNLOAD_ZIP_BUTTON_COOLDOWN_IN_MS);

      this.analyticsService
        .sendFileDownloadedEvent({
          from: 'download_all_button',
        })
        .subscribe();
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        this.isDownloadingZip.set(false);
        return;
      }

      console.error('Download failed', err);
      this.downloadError.set(
        this.translate.instant('PROMPT_INPUT.DOWNLOAD_ZIP_ERROR') as string,
      );
      this.isDownloadingZip.set(false);
    }
  }

  ngOnDestroy() {
    this.pollingSubscription?.unsubscribe();
    if (this.isDownloadingZip()) {
      this.isDownloadingZip.set(false);
    }
  }
}
